import { Error as ErrorDisplay, LetterAvatar, PageLoading } from '@/components/shared';
import { useCurrentMatter } from '@/components/contexts/CurrentMatterContext';
import { useCaseContext } from '@/components/contexts/CaseContext';
import useMatterMembers from '@/hooks/useMatterMembers';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { backendClient } from '@/lib/api-client';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { mutate } from 'swr';

import UpdateMemberRole from './UpdateMemberRole';
import AddMember from './AddMember';
import type { Matter, MatterMember } from '@/types/base';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { useState } from 'react';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/NewButton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type MatterMemberRow = MatterMember & {
  id: string;
};

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase();

const Members = ({ matter }: { matter: Matter }) => {
  const { t } = useTranslation('common');
  const { canAccess } = useCurrentMatter();
  const { selectedCase, setSelectedCase } = useCaseContext();
  const navigate = useNavigate();
  const [selectedMember, setSelectedMember] = useState<MatterMember | null>(null);
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [leaveDialogVisible, setLeaveDialogVisible] = useState(false);
  const { user: sessionUser } = useAuth();
  const currentUser = sessionUser?.dbUser;

  const { isLoading, isError, members, mutateMatterMembers } = useMatterMembers(
    matter.guid
  );

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError) {
    return <ErrorDisplay message={isError.message} />;
  }

  const removeMatterMember = async (member: MatterMember | null) => {
    return new Promise<void>(async (resolve, reject) => {
      if (!member) {
        resolve();
        return;
      }

      try {
        const response = await backendClient.delete(
          `/matter/${matter.guid}/members`,
          { params: { member_guid: member.user_guid } }
        );

        if (response.data.error) {
          toast.error(response.data.error.message);
          reject(new Error(response.data.error.message));
          return;
        }

        mutateMatterMembers();
        toast.success(t('member-deleted'));
        resolve();
      } catch (error) {
        toast.error(t('error-removing-member'));
        reject(error);
      }
    });
  };

  const leaveMatter = async () => {
    if (!currentUser) return;
    const memberGuid = currentMember?.user_guid ?? currentUser.guid;
    try {
      await backendClient.delete(`/matter/${matter.guid}/members`, {
        params: { member_guid: memberGuid },
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('error-leaving-matter'));
      return;
    }

    toast.success(t('leave-matter-success'));
    // We just lost access to this matter. Clear the cached matter lists so /home
    // refetches them on mount instead of briefly rendering the matter we just left.
    await Promise.all(
      ['/matter/get_active', '/matter/get_inactive', '/matter/tenant_matters'].map(
        (key) => mutate(key, undefined, { revalidate: false }),
      ),
    );
    // Drop the persisted matter selection if it points at the one we just left,
    // otherwise the sidebar (TeamNavigation) and background sync keep targeting a
    // matter we can no longer access. This also fires `selectedCaseChanged`, which
    // resets the background sync params.
    if (selectedCase === matter.guid) {
      setSelectedCase(undefined);
    }
    navigate({ to: '/home' });
  };

  const isCurrentUserMember = (member: MatterMember) => {
    if (!currentUser) {
      return false;
    }

    if (currentUser.guid === member.user_guid) {
      return true;
    }

    const currentUserEmail = normalizeEmail(currentUser.email);
    const memberEmail = normalizeEmail(member.email);
    return !!currentUserEmail && currentUserEmail === memberEmail;
  };

  // Function to check if current user can update a member's role
  const canUpdateRole = (member: MatterMember) => {
    // Must have 'update' permission on 'team_member' resource
    return !isCurrentUserMember(member) && canAccess('team_member', ['update']);
  };

  // Function to check if current user can remove a member
  const canRemoveMember = (member: MatterMember) => {
    // Must have 'delete' permission on 'team_member' resource
    return !isCurrentUserMember(member) && canAccess('team_member', ['delete']);
  };

  const memberRows: MatterMemberRow[] = (members ?? []).map((member) => ({
    ...member,
    id: member.user_guid,
  }));

  const currentMember = (members ?? []).find(isCurrentUserMember) ?? null;
  const ownerCount = (members ?? []).filter((m) => m.role === 'Owner').length;
  // An owner can't abandon a matter that would then have no owner; they must
  // hand ownership over first. The backend enforces this too.
  const isOnlyOwner = currentMember?.role === 'Owner' && ownerCount <= 1;

  const columns: ColumnDef<MatterMemberRow>[] = [
    {
      accessorKey: 'name',
      header: t('name'),
      cell: ({ row }) => (
        <div className="flex items-center justify-start space-x-2">
          <LetterAvatar name={row.original.name} />
          <span>{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: t('email'),
    },
    {
      accessorKey: 'role',
      header: t('role'),
      cell: ({ row }) =>
        canUpdateRole(row.original) ? (
          <UpdateMemberRole
            matter={matter}
            member={row.original}
            onSuccess={mutateMatterMembers}
          />
        ) : (
          <span>{row.original.role}</span>
        ),
    },
  ];

  // Keep every action button the same width so "Leave matter" and "Remove" line
  // up across rows instead of jumping around.
  const actionButtonClassName = 'w-24 px-2';

  if (canAccess('team_member', ['delete']) || !!currentMember) {
    columns.push({
      id: 'actions',
      header: t('actions'),
      cell: ({ row }) => {
        // Own row: always show "Leave", greyed out with a reason when it can't be
        // done (you're the matter's only owner).
        if (isCurrentUserMember(row.original)) {
          return isOnlyOwner ? (
            // Self-contained provider: a disabled button swallows hover events, so
            // the tooltip has to hang off the wrapping <span>. Providing the tooltip
            // context here keeps the members table from depending on an ancestor
            // TooltipProvider (Radix throws if a Tooltip renders without one).
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled
                      className={actionButtonClassName}
                      text={t('leave-matter')}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px]">
                  {t('leave-matter-only-owner')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setLeaveDialogVisible(true)}
              className={actionButtonClassName}
              text={t('leave-matter')}
            />
          );
        }

        // Other members: owners can remove them.
        return canRemoveMember(row.original) ? (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              setSelectedMember(row.original);
              setConfirmationDialogVisible(true);
            }}
            className={actionButtonClassName}
            text={t('remove')}
          />
        ) : null;
      },
    });
  }
  
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="space-y-3">
          <h2 className="text-xl font-medium leading-none tracking-tight">
            {t('members-description')}
          </h2>
        </div>
        {canAccess('team_member', ['create']) && (
          <AddMember 
            matter={matter} 
            onSuccess={mutateMatterMembers} 
          />
        )}
      </div>

      <DataTable columns={columns} data={memberRows} hideSearch />

      <ConfirmationDialog
        visible={confirmationDialogVisible}
        onCancel={() => setConfirmationDialogVisible(false)}
        onConfirm={() => removeMatterMember(selectedMember)}
        title={t('confirm-delete-member')}
      >
        {t('delete-member-warning', {
          name: selectedMember?.name,
          email: selectedMember?.email,
        })}
      </ConfirmationDialog>

      <ConfirmationDialog
        visible={leaveDialogVisible}
        onCancel={() => setLeaveDialogVisible(false)}
        onConfirm={leaveMatter}
        title={t('leave-matter')}
        confirmText={t('leave-matter')}
        submittingText={t('leaving')}
      >
        {t('leave-matter-confirmation')}
      </ConfirmationDialog>
    </div>
  );
};

export default Members;
